import { useEffect, useState } from "react";
import {
  youtubeSay,
  stageJitsiOnly,
  JITSI_ONLY_SAY,
  WHALEON_BROWSER_SAY,
  WHALE_DOWNLOAD_URL,
  meetKind,
  meetKindShown,
  type MeetKind,
} from "@greg-chick/shared";
import { getEvents, saveEvents, checkMeetUrl, getNotices, type EventDoc } from "../lib/contentApi";
import type { MeetVerdict } from "@greg-chick/shared";
import { useRegisterSave } from "../lib/saveBar";
import { Msg } from "./ui";
import { MeetPicker } from "./MeetPicker";
import { eventRemoveWarning } from "../lib/eventRemove";
import { isoFromKstDateTime, kstDateTimeInput } from "@greg-chick/shared"; // 🕘 한국 시각 — 기기 시간대와 상관없이(kstTime.ts · 2026-10-16)

/**
 * 행사 일정 편집(FR-EVENT) — 랜딩 배너·커뮤니티 "다가오는 행사"에 그대로 반영된다.
 *
 * 왜 만들었나: 지금까지 행사는 `content/events.yaml` 을 고치고 빌드·재배포해야 바뀌었다
 * (비개발자 갱신 불가 — CLAUDE.md 원칙 위반). 여기 저장하면 게시본이 정적 파일을 덮어쓴다.
 * 화상무대·유튜브 폴백은 접어 둔다(대부분의 행사는 제목·기간·신청폼이면 충분).
 * 🔴 다만 **이미 화상 무대를 쓰는 행사면 열어 둔다** — 접힌 탓에 운영자가 「회의 주소」 칸을
 *    못 보고, 행사가 여전히 Jitsi(5분 뒤 끊김)로 열리고 있었다(2026-09-03 신고).
 */
export function EventPanel() {
  const [list, setList] = useState<EventDoc[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  /**
   * 🔎 **붙여넣은 주소를 서버가 한 번 열어 본 결과**(행사 자리번호 → 한 줄).
   * 🔴 운영자가 「없는 주소」를 **행사 당일에** 알게 되는 걸 막는다(2026-09-03 신고).
   */
  const [checked, setChecked] = useState<Record<number, MeetVerdict | null>>({});
  const [checking, setChecking] = useState<number | null>(null);
  /**
   * 🏠 **운영자가 정해 둔 기본 회의 주소.** 이게 있으면 행사를 비워 둬도 그 방으로 열린다 —
   * 🔴 이걸 안 보면 기본 방을 넣어 두고도 여기서 계속 「화상이 안 열려요」라고 잔소리한다.
   */
  const [siteMeet, setSiteMeet] = useState<string | null>(null);
  /**
   * 🎥 **행사마다 어느 서비스를 보고 있나**(자리번호 → 갈래).
   *
   * 🔴 효과로 맞추지 않는다 — 목록이 새로 오면 같이 돌면서 방금 고른 것을 되돌린다.
   * 붙여넣은 주소가 아는 서비스면 그것이 이기고, 아니면 운영자가 고른 것(`kindOfRow`).
   */
  const [pickedKind, setPickedKind] = useState<Record<number, MeetKind | null>>({});

  /** 이 행사 줄이 지금 보여 줄 갈래. 화면 여러 자리가 **같은 답**을 쓰도록 한 곳에서 낸다. */
  function kindOfRow(i: number, url: string | undefined): MeetKind {
    // 🔴 판정은 shared 한 곳(`meetKindShown`) — 화면마다 짜서 갈렸던 자리다
    return meetKindShown(url, pickedKind[i] ?? null);
  }

  async function checkMeet(i: number, url: string) {
    setChecking(i);
    const v = await checkMeetUrl(url);
    setChecking(null);
    // 못 물어봤으면 아무 말도 안 한다 — 「없다」과 「못 물어봤다」는 다르다
    setChecked((prev) => ({ ...prev, [i]: v }));
  }

  useEffect(() => {
    getEvents()
      .then((e) => {
        setList(e);
        setLoaded(true);
      })
      .catch(() => setMsg("엣지 서버에 연결하지 못했어요. 서버가 떠 있는지 확인해 주세요."));
    // 🔴 못 읽으면 null — 「기본 방이 없다」로 보고 경고한다(있는데 못 읽는 쪽보다 안전하다)
    getNotices()
      .then((n) => setSiteMeet(n.defaultMeetUrl || null))
      .catch(() => setSiteMeet(null));
  }, []);

  function update(i: number, patch: Partial<EventDoc>) {
    setList((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  }
  function updateStage(i: number, patch: Partial<NonNullable<EventDoc["stage"]>>) {
    setList((prev) =>
      prev.map((e, idx) => (idx === i ? { ...e, stage: { ...e.stage, ...patch } } : e)),
    );
  }
  function add() {
    // 새 행사는 **꺼진 채로** 시작한다 — 적는 도중에 아이 화면으로 새어 나가지 않게.
    // 다 적고 나서 [아이 화면에 보이기] 를 켠다.
    setList((prev) => [{ title: "", start: "", end: "", visible: false }, ...prev]);
  }
  function remove(i: number) {
    // 🎪 아이에게 보이는(진행 중·다가오는) 행사면 한 번 묻는다 — 저장은 통째 바꾸기라 묻지 않고 사라졌다
    const target = list[i];
    const warn = target ? eventRemoveWarning(target, Date.now()) : null;
    if (warn && !window.confirm(warn)) return;
    setList((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function onSave() {
    // 제목 없는 행은 서버도 버리지만, 몇 개가 빠졌는지 운영자에게 정확히 알려준다
    const clean = list.filter((e) => e.title.trim());
    const dropped = list.length - clean.length;
    const count = await saveEvents(clean);
    setList(clean);
    setMsg(
      `✅ 행사 ${count}개를 올렸어요. 메인 배너와 [커뮤니티]에 바로 보여요.` +
        (dropped > 0 ? ` (제목이 빈 ${dropped}개는 저장하지 않았어요)` : ""),
    );
  }
  // 저장은 화면 아래 고정 바 하나로 모인다(이 탭엔 패널이 셋인데 버튼이 셋이었다).
  useRegisterSave({
    id: "event",
    label: "행사 일정",
    save: onSave,
    draft: { data: () => list, restore: (d) => setList(d as EventDoc[]) },
  });

  return (
    <section className="panel event-panel">
      <header className="panel__head">
        <h2>🎪 행사 일정</h2>
        <p>
          <b>[아이 화면에 보이기]를 켠 행사만</b> 메인 배너와 [커뮤니티]에 나와요. 진행 중이면
          "지금 열리고 있어요", 아직이면 "다가오는 행사"로 자동 표시돼요.
          <br />
          준비 중인 행사는 꺼 둔 채로 미리 적어 두세요.
        </p>
      </header>

      <div className="event-panel__actions">
        {/**
          * 🔴 **목록을 못 읽었으면 추가도 못 하게 한다.** 저장은 **전체 교체**라,
          * 못 읽은 상태에서 하나 만들어 저장하면 게시본이 그 하나로 바뀐다.
          * 엣지가 그 저장을 거절하게 해 뒀지만(409), 여기서 먼저 막는 편이 낫다 —
          * 운영자가 공들여 적은 뒤에 거절당하는 것보다.
          */}
        <button className="btn" onClick={add} disabled={!loaded}>
          + 새 행사 추가
        </button>
      </div>

      <Msg text={msg} />
      {loaded && list.length === 0 ? (
        <p className="event-panel__empty">
          등록된 행사가 없어요. [+ 새 행사 추가]로 시작하세요. (없으면 배너도 안 보여요)
        </p>
      ) : null}

      <ul className="event-panel__list">
        {list.map((e, i) => (
          <li key={i} className="event-edit">
            <input
              className="event-edit__title"
              value={e.title}
              placeholder="행사 이름 (예: 온라인 북토크)"
              onChange={(ev) => update(i, { title: ev.target.value })}
            />
            {/* 아이 화면에 내보낼지 — 판정은 shared `visibleEvents` 한 곳이고 여기는 그 값을 켜고 끈다.
                값이 없으면 보이는 것으로 본다(이미 올려둔 행사가 갑자기 사라지면 안 된다). */}
            <label className="event-edit__show">
              <input
                type="checkbox"
                checked={e.visible !== false}
                onChange={(ev) => update(i, { visible: ev.target.checked })}
              />
              <b>아이 화면에 보이기</b>
              <span className={e.visible !== false ? "event-edit__on" : "event-edit__off"}>
                {e.visible !== false ? "지금 보여요" : "지금은 안 보여요"}
              </span>
            </label>
            <div className="event-edit__row">
              <label>
                시작
                <input
                  type="datetime-local"
                  value={toLocal(e.start)}
                  onChange={(ev) => update(i, { start: fromLocal(ev.target.value) })}
                />
              </label>
              <label>
                종료
                <input
                  type="datetime-local"
                  value={toLocal(e.end)}
                  onChange={(ev) => update(i, { end: fromLocal(ev.target.value) })}
                />
              </label>
            </div>
            <label className="event-edit__wide">
              신청 폼 주소(https, 선택)
              <input
                type="url"
                value={e.applyUrl ?? ""}
                placeholder="https://forms.gle/… (비우면 신청 버튼이 안 보여요)"
                onChange={(ev) => update(i, { applyUrl: ev.target.value || undefined })}
              />
            </label>

            {/**
              * 🔴 **이미 화상 무대를 쓰는 행사면 접어 두지 않는다.**
              *
              * 「고급」이라 접어 둔 탓에, 새로 만든 「회의 주소」 칸을 운영자가 **못 보고**
              * 예전 Jitsi 방 이름만 남아 있었다 — 행사가 여전히 5분 뒤 끊기는 상태로
              * 게시돼 있었다(2026-09-03 운영자 신고: 「북토크의 화상회의가 여전히 jitsi 로 열려」).
              * 안 쓰는 사람에겐 그대로 접혀 있고, **쓰는 사람에겐 열려 있다.**
              */}
            <details className="event-edit__adv" open={!!e.stage?.jitsi || !!e.stage?.meetUrl}>
              <summary>
                화상 무대(온라인 행사만)
                {stageJitsiOnly(e.stage, siteMeet) ? <b className="event-edit__warn"> ⚠️ 고쳐 주세요</b> : null}
              </summary>
              <div className="event-edit__row">
                {/**
                  * 🎥 **회의 주소를 붙여넣는 자리.** 웨일온·줌·구글밋 무엇이든 된다.
                  *
                  * 🔴 예전엔 Jitsi 방 이름만 받아 화면에 **끼워** 넣었는데, 8x8 은 끼워 쓰는 것을
                  * 데모로만 허용해서 **5분 뒤 통화가 끊긴다**(2026-09-02 운영자가 행사 중에
                  * 그 안내를 받았다). 이제 주소를 받아 **새 창으로** 연다 — 시간 제한도,
                  * 끼운 화면의 카메라·마이크 권한 문제도 없다.
                  */}
                <label>
                  {/**
                   * 🔴 **「새 창으로」를 이름에 못 박지 않는다.** 웨일온·줌은 우리 화면 **안에서**
                   * 열린다(실측). 이름이 늘 「새 창」이라고 하면 운영자는 웨일온을 고르고도
                   * 새 창으로 열릴 거라 믿는다 — 어느 쪽인지는 아래 스펙 표가 말해 준다.
                   */}
                  회의 주소
                  <input
                    type="url"
                    value={e.stage?.meetUrl ?? ""}
                    placeholder={meetKind(kindOfRow(i, e.stage?.meetUrl)).placeholder}
                    onChange={(ev) => {
                      updateStage(i, { meetUrl: ev.target.value || undefined });
                      // 🔴 다시 치면 고른 것을 비운다 — 붙여넣은 주소가 갈래를 다시 정하게
                      setPickedKind((prev) => ({ ...prev, [i]: null }));
                    }}
                  />
                  <small className="event-edit__hint">
                    아래에서 서비스를 고르고, 그 방의 <b>초대 링크</b>를 붙여넣으세요. 참여자는
                    계정 없이 링크로 들어와요.
                    <b> 이 칸을 채워야 화상이 열려요</b> — 비우면 열리지 않아요.
                    붙여넣고 아래 <b>[🔎 주소 확인]</b>을 누르면 진짜 열리는지 알려 드려요.
                  </small>
                  {/**
                    * 🎥 **고르는 자리.** 목록·스펙·걸음은 shared 한 곳(`MEET_KINDS`) —
                    * 선생님 화면과 **같은 말**을 한다. 갈리면 한쪽만 고쳐진다.
                    */}
                  <MeetPicker
                    kind={kindOfRow(i, e.stage?.meetUrl)}
                    onKind={(k) => setPickedKind((prev) => ({ ...prev, [i]: k }))}
                    onFill={(u) => {
                      updateStage(i, { meetUrl: u });
                      setPickedKind((prev) => ({ ...prev, [i]: null }));
                    }}
                  />
                  {/* 🐳 웨일온을 골랐을 때만 브라우저 이야기를 한다 — 늘 뜨는 안내는 안 읽는다 */}
                  {kindOfRow(i, e.stage?.meetUrl) === "whaleon" ? (
                    <p className="event-edit__note">
                      🐳 {WHALEON_BROWSER_SAY}{" "}
                      {/* 🔴 「받아야 해요」라고만 하면 검색하러 나갔다가 엉뚱한 걸 받는다 */}
                      <a href={WHALE_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
                        웨일 브라우저 받기 ↗
                      </a>
                    </p>
                  ) : null}
                  {/* 🔴 비어 있으면 **무슨 일이 나는지**를 바로 말한다 — 이 칸을 못 본 채
                      게시하면 행사 도중에 통화가 끊긴다(운영자가 행사 중에 거기까지 갔다). */}
                  {stageJitsiOnly(e.stage, siteMeet) ? (
                    <small className="event-edit__warn" role="status">
                      ⚠️ {JITSI_ONLY_SAY}
                      {/**
                        * 🔴 **말만 하지 않는다.** 「옮겨 주세요」라고만 하면 운영자는 방을
                        * 새로 만들러 간다 — 이미 참가자에게 알려 준 방인데도. 여기 있는
                        * 방 이름으로 **주소를 만들어 한 번에 옮긴다**(같은 방이다).
                        *
                        * 이름을 바꾸지는 않는다 — 바꾸면 그날 아무도 못 들어온다.
                        * 짧은 이름이 위험한 건 아래 `eventRoomRisky` 가 따로 말한다.
                        */}
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() =>
                          updateStage(i, { meetUrl: `https://meet.jit.si/${e.stage!.jitsi!}` })
                        }
                      >
                        ↪ 이 방을 「회의 주소」로 옮기기
                      </button>
                    </small>
                  ) : null}
                  {/**
                    * 🔎 **붙여넣는 자리에서 바로 확인한다.**
                    * 운영자가 「없는 주소」를 행사 당일에 알게 되는 걸 막는다 —
                    * 끜워 넣기가 되는지도 여기서 알려 준다(교차출처라 화면은 못 읽는다).
                    */}
                  {e.stage?.meetUrl ? (
                    <div className="event-edit__check">
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={checking === i}
                        onClick={() => void checkMeet(i, e.stage!.meetUrl!)}
                      >
                        {checking === i ? "확인 중…" : "🔎 주소 확인"}
                      </button>
                      {checked[i] ? (
                        <small
                          className={
                            checked[i]!.kind === "ok" ? "event-edit__hint" : "event-edit__warn"
                          }
                          role="status"
                        >
                          {checked[i]!.kind === "ok" ? "✅" : "⚠️"} {checked[i]!.say}
                        </small>
                      ) : null}
                    </div>
                  ) : null}
                </label>
                {/**
                  * 🔴 **옛 Jitsi 방 이름 — 이젠 안 쓴다.**
                  *
                  * 화상 서비스는 운영자가 고른다(웨일온·구글밋·줌·Jitsi). 코드도 더 이상 이 값으로
                  * 되돌아가지 않는다. 그런데도 **새로 넣는 칸을 남겨 두면 또 채운다** —
                  * 그러면 운영자는 채웠다고 여기고, 행사 당일에야 안 열리는 걸 안다.
                  *
                  * 다만 **이미 들어 있는 값을 말없이 지우지는 않는다.** 참가자에게 이미
                  * 알려 준 이름일 수 있다. 값이 있을 때만 보여 주고 **지우는 단추**를 붙인다.
                  */}
                {e.stage?.jitsi ? (
                  <label>
                    옛 Jitsi 방 이름 (이젠 안 써요)
                    <input value={e.stage.jitsi} readOnly disabled />
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => updateStage(i, { jitsi: undefined })}
                    >
                      지우기
                    </button>
                    <small className="event-edit__hint">
                      이 방은 통화가 5분 뒤 끊겨서 더 이상 안 써요. 위 「회의 주소」를 채우시고
                      여긴 지우셔도 돼요.
                    </small>
                  </label>
                ) : null}
                <label>
                  유튜브 라이브(정원 초과 폴백)
                  <input
                    type="url"
                    value={e.stage?.youtubeLive ?? ""}
                    placeholder="https://youtu.be/…"
                    onChange={(ev) => updateStage(i, { youtubeLive: ev.target.value || undefined })}
                  />
                  {/* 🔴 **아이 화면이 쓰는 그 판정**으로 본다. 여기서 통과시킨 주소가 화면에서
                      「준비 중」이 되면 운영자는 다 해 놓은 줄 알고 넘어간다(shared/youtube.ts). */}
                  {youtubeSay(e.stage?.youtubeLive) ? (
                    <em className="warn">{youtubeSay(e.stage?.youtubeLive)}</em>
                  ) : null}
                </label>
                <label>
                  화상 정원
                  <input
                    type="number"
                    min={1}
                    value={e.stage?.capacity ?? ""}
                    placeholder="100"
                    onChange={(ev) =>
                      updateStage(i, { capacity: Number(ev.target.value) || undefined })
                    }
                  />
                </label>
              </div>
            </details>

            <div className="event-edit__foot">
              <button className="btn btn--sm" onClick={() => remove(i)}>삭제</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** ISO(+09:00) → datetime-local 입력값. 파싱 불가면 빈 값. */
function toLocal(iso?: string): string {
  return kstDateTimeInput(iso); // 🔴 예전엔 기기 시간대(getHours)로 풀어 다른 시간대 PC 에서 저장마다 9시간씩 당겨졌다
}
/** datetime-local → KST 명시 ISO(행사는 한국 기준 — 기기 시간대로 밀리지 않게). */
function fromLocal(v: string): string | undefined {
  return isoFromKstDateTime(v);
}
